import { useQuery } from '@tanstack/react-query';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { PlatformMetrics } from '@barbeiropro/contracts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import { Card, Kpis, microsMoney, pct } from './shared';

const FORMULAS: [string, string][] = [
  ['MRR (contratado)', 'Soma do preço mensal normalizado (plano anual ÷ 12) das assinaturas ativas; atrasadas entram se a configuração permitir. É valor contratado, não caixa.'],
  ['ARR (contratado)', 'MRR × 12.'],
  ['Churn de clientes', 'Cancelamentos no período ÷ assinaturas ativas no início do período.'],
  ['Churn de receita', 'MRR perdido no período ÷ MRR no início do período.'],
  ['Conversão de teste', 'Testes convertidos em pagantes ÷ testes encerrados no período.'],
  ['Inadimplência', 'MRR em atraso e carência ÷ MRR total.'],
  ['Recebido (realizado)', 'Soma das faturas pagas no período.'],
  ['Próximo mês (estimado)', 'MRR do início + novas − perdidas. Projeção, não é receita realizada.'],
  ['Custo de IA por barbearia', 'Soma do custo registrado nas requisições de IA da barbearia.'],
];

const PRESETS = ['30', '90', '365', 'year'] as const;
type Preset = (typeof PRESETS)[number];
function rangeOf(p: Preset) {
  const to = new Date();
  const from = p === 'year' ? new Date(to.getFullYear(), 0, 1) : new Date(to.getTime() - Number(p) * 86_400_000);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(from), to: iso(to) };
}

export function MetricsPage() {
  const [preset, setPreset] = useQueryState('periodo', parseAsStringLiteral(PRESETS).withDefault('30'));
  const range = rangeOf(preset);
  const q = useQuery({
    queryKey: ['platform', 'metrics', range],
    queryFn: () => api.get<PlatformMetrics>('/platform/metrics', range),
    placeholderData: (p) => p,
  });
  const m = q.data;
  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader title="Métricas" description="Contratado, realizado e estimado aparecem separados. As fórmulas estão no fim da página." />
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="m-range">Período</Label>
          <Select value={preset} onValueChange={(v) => setPreset(v as Preset)}>
            <SelectTrigger id="m-range" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
              <SelectItem value="365">Últimos 12 meses</SelectItem>
              <SelectItem value="year">Este ano</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {m && <p className="pb-2 text-sm text-muted-foreground">Período: {m.period.from} a {m.period.to}</p>}
      </div>
      {q.isLoading && <LoadingState />}
      {q.error && !m && <ErrorState error={q.error} onRetry={q.refetch} />}
      {m && (
        <>
          <h2 className="text-sm font-semibold text-muted-foreground">Contratado</h2>
          <Kpis
            items={[
              { label: 'MRR', value: money(m.mrrCents), hint: 'Contratado' },
              { label: 'ARR', value: money(m.arrCents), hint: 'Contratado' },
              { label: 'Assinaturas pagantes', value: String(m.activeCustomers) },
              { label: 'Inadimplência', value: pct(m.delinquencyRate), hint: 'Do MRR total' },
            ]}
          />
          <h2 className="text-sm font-semibold text-muted-foreground">Retenção</h2>
          <Kpis
            items={[
              { label: 'Churn de clientes', value: pct(m.customerChurnRate), hint: `${m.canceledInPeriod} cancelamentos` },
              { label: 'Churn de receita', value: pct(m.revenueChurnRate) },
              { label: 'Conversão de teste', value: pct(m.trialConversionRate), hint: `${m.trialsConverted} de ${m.trialsEnded} encerrados` },
              { label: 'Novas assinaturas', value: String(m.newSubscriptions) },
            ]}
          />
          <h2 className="text-sm font-semibold text-muted-foreground">Realizado e estimado</h2>
          <Kpis
            items={[
              { label: 'Recebido no período', value: money(m.receivedCents), hint: 'Realizado (faturas pagas)' },
              { label: 'Próximo mês', value: money(m.estimatedNextMonthCents), hint: 'Estimado' },
              { label: 'Custo de IA', value: microsMoney(m.aiCostMicrosTotal), hint: 'Total no período' },
            ]}
          />
          <Card title="Evolução mensal" description="Barras: recebido (realizado). Linha: MRR contratado.">
            <div className="h-72 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={m.series.map((s) => ({ ...s, mrr: s.mrrCents / 100, recebido: s.receivedCents / 100 }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} width={56} />
                  <Tooltip formatter={(v) => money(Math.round(Number(v) * 100))} />
                  <Legend />
                  <Bar dataKey="recebido" name="Recebido (realizado)" fill="var(--brass)" />
                  <Line dataKey="mrr" name="MRR (contratado)" stroke="var(--primary)" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="MRR por plano (contratado)">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-1 font-medium">Plano</th>
                      <th className="py-1 text-right font-medium">Assinantes</th>
                      <th className="py-1 text-right font-medium">MRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.mrrByPlan.map((p) => (
                      <tr key={p.planName} className="border-t">
                        <td className="py-1.5">{p.planName}</td>
                        <td className="tabular py-1.5 text-right">{p.subscribers}</td>
                        <td className="tabular py-1.5 text-right">{money(p.mrrCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="Custo de IA por barbearia">
              {m.aiCostByOrg.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem uso de IA registrado no período.</p>
              ) : (
                <ul className="divide-y text-sm">
                  {m.aiCostByOrg.map((o) => (
                    <li key={o.organizationId} className="flex justify-between gap-3 py-1.5">
                      <span className="min-w-0 truncate">{o.organizationName}</span>
                      <span className="tabular shrink-0">{microsMoney(o.costMicros)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
      <Card title="Fórmulas">
        <dl className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
          {FORMULAS.map(([k, v]) => (
            <div key={k}>
              <dt className="font-medium">{k}</dt>
              <dd className="text-muted-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
}
